<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-2">
      <h1 class="font-extrabold text-2xl">{{ t("resources.list.title") }}</h1>
      <div class="text-light-gray text-sm">
        {{ t("resources.list.subTitle") }}
      </div>
    </div>
    <div v-if="isLoading" class="flex h-[60vh] justify-center">
      <LoadingIcon :isLoading="isLoading" />
    </div>
    <div v-if="!isLoading" class="flex flex-col gap-4">
      <div class="grid grid-cols-4 gap-3 my-4">
        <InfoBox
          :title="t('resources.list.activeResources')"
          :value="resourceGeneralInformation!.activeResources"
          :backgroundColor="EventState.events[5].lightColor"
          :textColor="EventState.events[5].color"
        >
          <IconManager
            name="Box"
            :style="{ color: EventState.events[5].color }"
          />
        </InfoBox>

        <InfoBox
          :title="t('resources.list.available')"
          :value="resourceGeneralInformation!.available"
          :backgroundColor="EventState.events[4].lightColor"
          :textColor="EventState.events[4].color"
        >
          <IconManager
            name="Check"
            :style="{ color: EventState.events[4].color }"
          />
        </InfoBox>

        <InfoBox
          :title="t('resources.list.inUse')"
          :value="resourceGeneralInformation!.inUse"
          :backgroundColor="EventState.events[0].lightColor"
          :textColor="EventState.events[0].color"
        >
          <IconManager
            name="Play"
            :style="{ color: EventState.events[0].color }"
          />
        </InfoBox>

        <!-- <InfoBox
          :title="t('resources.list.inMaintenance')"
          :value="resourceGeneralInformation!.inMaintenance"
          :backgroundColor="EventState.events[2].lightColor"
          :textColor="EventState.events[2].color"
        >
          <IconManager
            name="Gear"
            :style="{ color: EventState.events[2].color }"
          />
        </InfoBox> -->

        <InfoBox
          :title="t('resources.list.averageCost')"
          :value="resourceGeneralInformation!.averageCost + ' €'"
          :backgroundColor="EventState.events[3].lightColor"
          :textColor="EventState.events[3].color"
        >
          <IconManager
            name="Euro"
            :style="{ color: EventState.events[3].color }"
          />
        </InfoBox>
      </div>

      <div class="flex flex-row gap-2 items-center bg-secondary p-4 rounded-xl">
        <IconManager name="Info" class="text-primary" />
        {{ t("resources.list.info") }}
      </div>

      <div class="flex flex-col gap-4" v-for="item in resources">
        <InfoCardView
          :data="item"
          :actions="[
            {
              icon: 'Eye',
              onClick: (r) =>
                go(RouteName.ResourceDetail, { id: r.resourceId }),
            },
          ]"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// Import required components and utilities
import { useToast } from "@/core/composables/use-toast";
import IconManager from "@/core/IconManager.vue";
import InfoBox from "@/core/components/InfoBox.vue";
import InfoCardView from "@/core/components/InfoCardView.vue";
import { ResourceType } from "@/modules/resources/models/resource-type";
import { useI18n } from "vue-i18n";
import { resourceRemoteDataSource } from "@/core/models/global-variables";
import { RouteName } from "@/core/models/route-name";
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { ResourceGeneralInformation } from "../models/resource-general-information";
import { EventState } from "@/modules/events/models/event-state";
import LoadingIcon from "@/core/components/LoadingIcon.vue";

const { t } = useI18n();
const { showError } = useToast();
const router = useRouter();
const isLoading = ref<boolean>(true);
const resources = ref<ResourceType[]>([]);
const resourceGeneralInformation = ref<ResourceGeneralInformation | null>(null);
onMounted(() => {
  loadInitialData();
});

async function loadInitialData() {
  isLoading.value = true;

  try {
    const [resourcesRes, generalInformationRes] = await Promise.all([
      resourceRemoteDataSource.getResources(),
      resourceRemoteDataSource.getGeneralInformation(),
    ]);
    if (!resourcesRes.isSuccess) {
      showError({
        message: resourcesRes.detail!,
        title: resourcesRes.title,
        statusCode: resourcesRes.statusCode,
      });
    }
    resources.value = resourcesRes.data;

    if (!generalInformationRes.isSuccess) {
      showError({
        message: generalInformationRes.detail!,
        title: generalInformationRes.title,
        statusCode: generalInformationRes.statusCode,
      });
    }
    resourceGeneralInformation.value = generalInformationRes.data;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoading.value = false;
  }
}

function go(name: string, params: Record<string, any> = {}) {
  router.push({
    name,
    params,
  });
}
</script>
